import { Users, DollarSign, TrendingUp, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import type { TripStatistics, TripState } from "@/types/eventPartner";

interface TripStatisticsCardProps {
  statistics: TripStatistics;
  isLoading?: boolean;
}

const STATUS_LABELS: Record<TripState, string> = {
  UNCLAIMED: "Unclaimed",
  BOOKED: "Booked",
  CLAIMED: "Claimed",
  DRIVER_ASSIGNED: "Driver Assigned",
  DRIVER_CONFIRMED: "Driver Confirmed",
  COMPLETED: "Completed",
  ABANDONED: "Abandoned",
  CANCELED: "Canceled",
};

const STATUS_COLORS: Record<TripState, string> = {
  UNCLAIMED: "bg-yellow-500/20 text-yellow-500",
  BOOKED: "bg-blue-500/20 text-blue-500",
  CLAIMED: "bg-purple-500/20 text-purple-500",
  DRIVER_ASSIGNED: "bg-indigo-500/20 text-indigo-500",
  DRIVER_CONFIRMED: "bg-cyan-500/20 text-cyan-500",
  COMPLETED: "bg-green-500/20 text-green-500",
  ABANDONED: "bg-orange-500/20 text-orange-500",
  CANCELED: "bg-red-500/20 text-red-500",
};

export const TripStatisticsCard = ({ statistics, isLoading }: TripStatisticsCardProps) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-4 bg-muted rounded w-20 mb-2" />
              <div className="h-8 bg-muted rounded w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const statusEntries = Object.entries(statistics.byTripState).filter(
    ([_, count]) => count > 0
  );

  return (
    <div className="space-y-4">
      {/* Main Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Users className="h-4 w-4" />
              <span className="text-sm">Total Trips</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {statistics.totalTrips}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <DollarSign className="h-4 w-4" />
              <span className="text-sm">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(statistics.totalRevenue)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-sm">Vehicle Types</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {statistics.byVehicleType.length}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Clock className="h-4 w-4" />
              <span className="text-sm">Avg. Price</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {statistics.totalTrips > 0
                ? formatCurrency(statistics.totalRevenue / statistics.totalTrips)
                : "$0"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Status Breakdown */}
      {statusEntries.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {statusEntries.map(([status, count]) => (
            <span
              key={status}
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                STATUS_COLORS[status as TripState]
              }`}
            >
              {STATUS_LABELS[status as TripState]}: {count}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
